import Vue from 'vue'
import { onMounted, onUnmounted, watch } from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { storeToRefs } from 'pinia'
import AuthService from '@/services/modules/AuthService'
import useAuthStore from '@/store/auth'
import useUserStore from '@/store/user'
import usePausaStore from '@/store/pausas'
import { EventBus } from '@/utils/EventBus'
import { clearStores } from '@/utils/store'

export default function useTokenValidation() {
  const authStore = useAuthStore()
  const { setAccessToken } = authStore
  const { accessToken, possuiAccessTokenDefinido } = storeToRefs(authStore)
  const { isLoggedIn } = storeToRefs(useUserStore())

  const { router } = useRouter()

  let socket = null
  let refreshTokenInterval = null

  function setRefreshAccessTokenInterval() {
    // Idempotente: evita armar um segundo setInterval se já tiver sido armado pelo onMounted ou pelo evento 'logged-in'
    if (refreshTokenInterval) return

    const UMA_HORA_E_50_MINUTOS = 110 * 60 * 1000

    refreshTokenInterval = setInterval(async () => {
      const newAccessToken = await AuthService.refreshToken(accessToken.value)

      setAccessToken(newAccessToken)
    }, UMA_HORA_E_50_MINUTOS)
  }

  function closeValidateTokenWebSocket() {
    if (!socket) return
    socket.close()
    socket = null
  }

  function getUserOutOfThePlatform() {
    closeValidateTokenWebSocket()
    clearStores()
    clearInterval(refreshTokenInterval)
    refreshTokenInterval = null
    router.push({ name: 'auth-login' })
  }

  function getUserOutOfThePlatformAndToast() {
    getUserOutOfThePlatform()
    Vue.$toast.warning('Sua sessão expirou. Faça o login novamente!')
  }

  async function logoutUser() {
    try {
      await AuthService.logoutAgente()
    } finally {
      getUserOutOfThePlatformAndToast()
    }
  }

  function openValidateTokenWebSocket() {
    if (socket) return

    socket = new WebSocket(`${process.env.VUE_APP_SECURITY_WSS}auth/validate/ws?token=${accessToken.value}`)

    socket.onmessage = event => {
      const data = JSON.parse(event.data)
      if (data.isAuthenticated) return
      logoutUser()
    }
  }

  function attemptToStartWebSocket() {
    const { id: pausaId } = usePausaStore()

    if (pausaId) return
    if (socket) return
    if (!isLoggedIn.value) return

    openValidateTokenWebSocket()
  }

  async function handleValidateAccessTokenAsync() {
    if (!possuiAccessTokenDefinido.value) return

    // Páginas de callback são usadas apenas para capturar um código de autorização, portanto não é necessário validar o token
    if (router.currentRoute.name === 'auth-code-flow-callback') return

    const isTokenValid = await AuthService.isAccessTokenValid()
    if (isTokenValid) return

    /*
     * Chamava getUserOutOfThePlatformAndToast() direto, sem registrar logout: a sessão ficava aberta
     * no banco e o agente continuava "disponível" para distribuição mesmo depois de expulso da tela.
     * logoutUser() tenta o logout e, se falhar, ainda assim expulsa e avisa (try/finally).
     */
    await logoutUser()
  }

  onMounted(async () => {
    await handleValidateAccessTokenAsync()
    attemptToStartWebSocket()

    /*
     * O timer só era armado no evento 'logged-in', disparado pela ação de login. Quem dá F5 ou abre
     * o site em aba nova carrega o App.vue (e este composable) com a sessão já persistida em
     * localStorage, sem passar pelo login de novo: sem isto, o timer nunca é armado e a sessão é
     * derrubada em 2h, mesmo com uso contínuo.
     */
    if (isLoggedIn.value) setRefreshAccessTokenInterval()
  })

  onUnmounted(() => {
    clearInterval(refreshTokenInterval)
  })

  EventBus.$on('logged-in', () => {
    attemptToStartWebSocket()
    setRefreshAccessTokenInterval()
  })
  EventBus.$on('logged-out', getUserOutOfThePlatform)

  function mostrarPopUpPerguntandoSeQuerFecharAbaOuJanela(e) {
    e.preventDefault()
    e.returnValue = ''

    // setShowFecharSiteDialog(true)
  }

  watch(
    possuiAccessTokenDefinido,
    v => {
      if (!v) {
        // setShowFecharSiteDialog(false)
        // window.removeEventListener('beforeunload', mostrarPopUpPerguntandoSeQuerFecharAbaOuJanela)
      } else {
        /*
         * Navegadores modernos não permitem mais mostrar mensagens customizadas no dialog de confirmação, por isso não setei o 'returnValue' nem retornei uma nova string
         * A ideia inicial seria mostrar apenas o dialog customizado do Vuetify, mas isso não é possível, é necessário mostrar o do navegador, e ambos não podem aparecer juntos
         * TODO: mostrar primeiro o dialog customizado depois o do navegador, ou vice-versa
         */

        // window.addEventListener('beforeunload', mostrarPopUpPerguntandoSeQuerFecharAbaOuJanela)
      }
    },
    {
      immediate: true,
    },
  )

  return { closeValidateTokenWebSocket, openValidateTokenWebSocket }
}
